import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { X, Building, Mail, Globe } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface CreateOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (orgId: string) => void;
}

export const CreateOrganizationModal: React.FC<CreateOrganizationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();

  const handleCreate = async () => {
    if (!name.trim() || !ownerEmail.trim()) {
      toast({
        title: "Validation Error",
        description: "Organization name and owner email are required",
        variant: "destructive",
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(ownerEmail)) {
      toast({
        title: "Validation Error",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    setIsCreating(true);
    try {
      const { data, error } = await supabase.functions.invoke('orgs-manage', {
        body: {
          action: 'create_org',
          name: name.trim(),
          domain: domain.trim() || undefined,
          owner_email: ownerEmail.trim()
        }
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: `Organization "${name}" created successfully`,
      });

      onSuccess(data.org_id);
      handleClose();
    } catch (error: any) {
      console.error('Create organization error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create organization",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setName('');
    setDomain('');
    setOwnerEmail('');
    setIsCreating(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Building className="h-5 w-5" />
              Create Organization
            </CardTitle>
            <CardDescription>
              Create a new organization to manage workspaces and users
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            disabled={isCreating}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="org-name" className="flex items-center gap-1">
              <Building className="h-3 w-3" />
              Organization Name*
            </Label>
            <Input
              id="org-name"
              placeholder="Enter organization name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isCreating}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="org-domain" className="flex items-center gap-1">
              <Globe className="h-3 w-3" />
              Domain (optional)
            </Label>
            <Input
              id="org-domain"
              placeholder="example.com"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              disabled={isCreating}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="owner-email" className="flex items-center gap-1">
              <Mail className="h-3 w-3" />
              Owner Email*
            </Label>
            <Input
              id="owner-email"
              type="email"
              placeholder="admin@example.com"
              value={ownerEmail}
              onChange={(e) => setOwnerEmail(e.target.value)}
              disabled={isCreating}
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={handleClose} disabled={isCreating}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreate} 
              disabled={isCreating || !name.trim() || !ownerEmail.trim()}
            >
              {isCreating ? "Creating..." : "Create Organization"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};